import type { ApiKeyDto } from '@nsb/core';
import { useState } from 'react';
import { api, errorText, useAction, useApi } from '../../api.ts';
import { Button, Confirm, ErrorLine, Field, Section, TextInput } from '../../components/ui.tsx';
import { dateWords } from '../../days.ts';

/**
 * Settings → Development tools → A key for Claude (Jason, 2026-10-05: "Yeah do
 * the key option"). Claude never signs in to production: an admin makes a key
 * here, sees it once, and puts it in `.env` on the machine Claude works on, as
 * NSB_PRODUCTION_KEY (`npm run builder`). It reaches the tree builder's draft
 * and nothing else (API `domain/keys.ts`); revoked, it stops at once, and it is
 * kept on the record. Admins only.
 */

const when = (iso: string) => dateWords(iso, 'moment');

export function KeysSection() {
  const { data: keys, error } = useApi<ApiKeyDto[]>(['keys'], '/keys');
  const [name, setName] = useState('');
  const [made, setMade] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const make = useAction((b: { name: string }) => api<{ id: string; key: string }>('/keys', { method: 'POST', body: b }), {
    invalidate: [['keys']],
    onSuccess: (r) => {
      setMade(r.key);
      setCopied(false);
      setName('');
    },
  });
  const revoke = useAction((id: string) => api(`/keys/${id}/revoke`, { method: 'POST', body: {} }), { invalidate: [['keys']], onSuccess: () => setRevoking(null) });
  const line = made ? `NSB_PRODUCTION_KEY=${made}` : '';

  return (
    <Section title="A key for Claude">
      <p className="max-w-measure text-ink-2">
        Lets Claude read and save the tree builder’s draft from your computer, without signing in. It reaches nothing else. Revoke it here and it stops at once.
      </p>

      {made ? (
        <div className="mt-4 max-w-measure border-l-2 border-ink bg-sheet px-4 py-3">
          <p>
            Your new key, shown this once. Put this line in the file <span className="num">.env</span> at the top of the repository on the computer Claude works on:
          </p>
          <code className="num mt-2 block break-all rounded-[2px] border border-rule bg-paper px-3 py-2 text-[0.8125rem]">{line}</code>
          <div className="mt-2 flex flex-wrap items-center gap-x-6">
            <Button
              tone="quiet"
              onClick={() => {
                void navigator.clipboard.writeText(line).then(
                  () => setCopied(true),
                  () => setCopied(false),
                );
              }}
            >
              {copied ? 'Copied' : 'Copy the line'}
            </Button>
            <Button tone="quiet" onClick={() => setMade(null)}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="mt-4 flex max-w-measure flex-wrap items-end gap-x-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) make.mutate({ name: name.trim() });
          }}
        >
          <div className="min-w-56 flex-1">
            <Field label="Name">{(id) => <TextInput id={id} value={name} maxLength={80} placeholder="The machine it is for" onChange={(e) => setName(e.target.value)} />}</Field>
          </div>
          <div className="mb-5">
            <Button type="submit" tone="secondary" busy={make.isPending} disabled={!name.trim()}>
              Make a key
            </Button>
          </div>
        </form>
      )}
      <ErrorLine error={errorText(make.error)} />

      {error ? <p className="mt-3 text-alert">{error.message}</p> : null}
      {keys?.length ? (
        <ul className="mt-4 divide-y divide-rule border-y border-rule">
          {keys.map((k) => (
            <li key={k.id} className="py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-x-6">
                <span>
                  {k.name} <span className="num meta">{k.prefix}…</span>
                </span>
                {k.revokedAt ? (
                  <span className="meta">Revoked {when(k.revokedAt)}</span>
                ) : revoking === k.id ? null : (
                  <Button tone="danger" onClick={() => setRevoking(k.id)}>
                    Revoke
                  </Button>
                )}
              </div>
              <p className="meta mt-0.5">
                Made by {k.createdBy}, {when(k.createdAt)} · {k.lastUsedAt ? `last used ${when(k.lastUsedAt)}` : 'not used yet'}
              </p>
              {revoking === k.id ? (
                <>
                  <Confirm prompt={`Revoke ${k.name}? Anything using it stops at once.`} busy={revoke.isPending} onYes={() => revoke.mutate(k.id)} onNo={() => setRevoking(null)} />
                  <ErrorLine error={errorText(revoke.error)} />
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}
